import { Button, Card, CardContent, Separator, Skeleton } from '@paguz/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  BookOpenIcon,
  ChevronRightIcon,
  ClipboardListIcon,
  FileTextIcon,
  LogOutIcon,
  MessageSquareWarningIcon,
} from 'lucide-react';
import { useState } from 'react';
import { logout } from '../../api/auth.ts';
import { QueryErrorState } from '../../components/feedback.tsx';
import { PageHeader } from '../../components/layout.tsx';
import { formatPhone } from '../../lib/format.ts';

import { useMe } from './use-me.ts';

export { useMe };

export function ProfilePage() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  return (
    <>
      <PageHeader title="Mi perfil" />
      {me.isPending ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : me.isError ? (
        <QueryErrorState error={me.error} onRetry={() => void me.refetch()} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <Row label="Nombre" value={me.data.fullName} />
            <Separator />
            <Row label="Negocio" value={me.data.businessName ?? '—'} />
            <Separator />
            <Row label="Documento" value={`${me.data.documentType} ${me.data.maskedDocument}`} />
            <Separator />
            <Row label="Celular" value={formatPhone(me.data.phone)} />
            {me.data.linkedAccounts.map((account) => (
              <div key={account.id} className="contents">
                <Separator />
                <Row
                  label="Cuenta vinculada"
                  value={`${account.participantName} ${account.maskedNumber}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <nav aria-label="Más opciones" className="mt-6 flex flex-col">
        {(
          [
            ['/reclamos', 'Mis reclamos', MessageSquareWarningIcon],
            ['/extracto', 'Mi extracto', FileTextIcon],
            ['/solicitud', 'Mi solicitud de crédito', ClipboardListIcon],
            ['/guia', 'Guía del demo', BookOpenIcon],
          ] as const
        ).map(([to, label, Icon]) => (
          <Link
            key={to}
            to={to}
            className="-mx-2 flex min-h-14 items-center gap-3 rounded-lg px-2 hover:bg-accent"
          >
            <Icon className="size-5 text-muted-foreground" aria-hidden />
            <span className="flex-1 font-medium">{label}</span>
            <ChevronRightIcon className="size-5 text-muted-foreground" aria-hidden />
          </Link>
        ))}
      </nav>
      <Button
        variant="outline"
        size="lg"
        className="mt-6 w-full"
        loading={busy}
        onClick={() => {
          setBusy(true);
          void logout().finally(() => {
            queryClient.clear();
            void navigate({ to: '/ingresar', replace: true });
          });
        }}
      >
        <LogOutIcon aria-hidden />
        Cerrar sesión
      </Button>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
